import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Carousel, Skeleton, message } from 'antd';
import { ArrowLeftOutlined, ShoppingCartOutlined } from '@ant-design/icons';
import httpClient from '../../utils/HttpClient';
import { PRODUCT_URL } from '../../constants/Url';
import { addToCartRequest, updateQuantityRequest } from '../../modules/shop/actions';
import { useTranslation } from 'react-i18next';

import { useSelector } from 'react-redux';
import { setProductDetailAction } from '../../modules/dataWebsite/actions';

export default function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  
  const product = useSelector(state => state.dataWebsiteReducer?.productDetailData);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const res = await httpClient.get(`${PRODUCT_URL}/${id}`);
        if (res.data) {
          const productData = res.data.data || res.data;
          setProductDetailAction(productData);
        } else {
          message.error(t('text_failed_loading_product_details'));
          setProductDetailAction(null);
        }
      } catch (error) {
        console.error("Fetch product error", error);
        message.error(t('text_failed_loading_product_details'));
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id, t]);

  const { items } = useSelector(state => state.shop.cart);
  const [quantity, setQuantity] = useState(1);

  const handleAddToCart = () => {
    if (product) {
      const productId = product.productId;
      const existingItem = items?.find(item => item.productNo === productId);

      if (existingItem) {
        dispatch(updateQuantityRequest(existingItem._id, existingItem.qty + quantity, productId));
      } else {
        dispatch(addToCartRequest(productId, quantity));
      }
      message.success(t('text_added_to_cart'));
    }
  };

  const handleBuyNow = () => {
    if (product) {
      const buyNowItem = {
        _id: `temp-${Date.now()}`,
        productId: product.productId || product._id,
        productNo: product.productNo || product.productId,
        productName: product.name || product.title,
        description: product.description,
        unitPrice: product.price || 0,
        currency: 'CNY',
        qty: quantity,
        productImages: product.images && product.images.length > 0 ? product.images : [product.imageUrl || product.picUrl || ''],
        categoryId: product.categoryId || product.category
      };
      
      navigate('/checkout', { state: { buyNowItem } });
    }
  };

  if (loading) {
    return (
      <div className="w-full min-h-screen bg-[#fcfdfe] pb-24 md:pb-16 relative font-sans">
        
        {/* Top Navigation Bar / Breadcrumb Area */}
        <div className="w-full max-w-[1263px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Skeleton.Input active size="small" style={{ width: '100px' }} />
        </div>

        <div className="w-full max-w-[1263px] mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row gap-10 lg:gap-16 items-start">
          
          {/* Left Column: Hero Image Carousel */}
          <div className="w-full md:w-[50%] lg:w-[55%] shrink-0 bg-gray-50 rounded-2xl overflow-hidden relative shadow-sm aspect-square flex items-center justify-center">
            <Skeleton.Image className="!w-[80%] !h-[80%]" active />
          </div>

          {/* Right Column: Product Details Section */}
          <div className="w-full md:w-[50%] lg:w-[45%] flex flex-col pt-2 md:pt-8 gap-8">
            
            <div className="border-b border-gray-100 pb-6 flex flex-col gap-4">
              <Skeleton.Input active size="large" style={{ width: '70%', height: '36px' }} />
              <Skeleton.Input active size="large" style={{ width: '120px', height: '40px' }} />
            </div>

            {/* Product Intro Box */}
            <div className="flex flex-col gap-3">
              <Skeleton.Input active size="small" style={{ width: '100px' }} />
              <div className="bg-gray-50 rounded-xl p-5 sm:p-6 border border-gray-100">
                <Skeleton active paragraph={{ rows: 4 }} title={false} />
              </div>
            </div>

            {/* Desktop Add to Cart */}
            <div className="hidden md:flex items-center gap-4 mt-auto">
              <Skeleton.Button active style={{ width: '56px', height: '56px', borderRadius: '0.75rem' }} />
              <Skeleton.Button active style={{ flex: 1, height: '56px', borderRadius: '0.75rem' }} className="flex-1" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) return <div className="p-20 text-center font-bold text-gray-500">{t("text_product_no_existed")}</div>;

  const images = product.images && product.images.length > 0 ? product.images : [product.imageUrl || product.picUrl || ''];

  return (
    <div className="w-full min-h-screen bg-[#fcfdfe] pb-24 md:pb-16 relative font-sans">
      <style>{`
        .custom-carousel .slick-dots {
          bottom: 12px;
        }
        .custom-carousel .slick-dots li button {
          background: none !important;
          // #fbc6d3
          opacity: 1 !important;
          width: 8px !important;
          height: 8px !important;
          border-radius: 50% !important;
        }
        .custom-carousel .slick-dots li.slick-active button {
          background: none !important; 
          // #ed0a3f
          width: 24px !important;
          border-radius: 4px !important;
        }
        
        /* Fix rich text editor output formatting overridden by Tailwind */
        .rich-text-content img {
          display: inline-block; /* override tailwind's block to allow floating */
          max-width: 100%;
          height: auto;
        }
        .rich-text-content p {
          margin-bottom: 1em;
        }
        .rich-text-content strong, .rich-text-content b {
          font-weight: bold;
        }
        .rich-text-content em, .rich-text-content i {
          font-style: italic;
        }
        .rich-text-content ul {
          list-style-type: disc;
          padding-left: 1.5em;
          margin-bottom: 1em;
        }
        .rich-text-content ol {
          list-style-type: decimal;
          padding-left: 1.5em;
          margin-bottom: 1em;
        }
        /* Support common rich text classes for alignment */
        .rich-text-content .ql-align-center, .rich-text-content .text-center { text-align: center; }
        .rich-text-content .ql-align-right, .rich-text-content .text-right { text-align: right; }
        .rich-text-content .ql-align-justify, .rich-text-content .text-justify { text-align: justify; }
      `}</style>
      
      {/* Top Navigation Bar / Breadcrumb Area */}
      <div className="w-full max-w-[1263px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div 
          onClick={() => navigate(-1)} 
          className="inline-flex items-center gap-2 cursor-pointer text-gray-500 hover:text-black transition-colors font-medium"
        >
          <ArrowLeftOutlined className="text-lg" />
          <span>{t('text_back_previous_page')}</span>
        </div>
      </div>

      <div className="w-full max-w-[1263px] mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row gap-10 lg:gap-16 items-start">
        
        {/* Left Column: Hero Image Carousel */}
        <div className="w-full md:w-[50%] lg:w-[55%] shrink-0 bg-white rounded-2xl overflow-hidden relative shadow-sm border-none" style={{maxHeight: '500px'}}>
          <Carousel autoplay dots={false} dotPosition="bottom" effect="fade" className="custom-carousel">
            {images.map((img, idx) => (
              <div key={idx} className="w-full aspect-square flex items-center justify-center">
                {img ? (
                  <img 
                    src={img} 
                    alt={`${product.name} ${idx}`} 
                    className="w-full object-cover" 
                    style={{ maxHeight: '500px', borderRadius: '16px' }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400 bg-gray-50">{("text_no_image")}</div>
                )}
              </div>
            ))}
          </Carousel>
        </div>

        {/* Right Column: Product Details Section */}
        <div className="w-full md:w-[50%] lg:w-[45%] flex flex-col pt-2 md:pt-8">
          
          <div className="mb-6 pb-6">
            <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 leading-tight mb-4 uppercase">
              {product.name || product.title}
            </h1>
            <div className="text-3xl sm:text-4xl font-black text-[#ed0a3f]">
              ¥ {(product.price || 0).toFixed(2)}
            </div>
          </div>

          <div className="mb-8 flex items-center gap-4">
            <span className="text-gray-900 font-bold text-lg">{t('text_number')}</span>
            <div className="flex items-center gap-3 ml-4">
              <button 
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                disabled={quantity <= 1}
                className={`w-10 h-10 rounded-lg border-2 flex items-center justify-center text-xl font-bold ${quantity > 1 ? 'hover:bg-gray-50 text-gray-700 cursor-pointer border-gray-200' : 'text-gray-300 bg-gray-50 border-gray-100 cursor-not-allowed'}`}
              >
                -
              </button>
              <span className="font-semibold text-xl w-10 text-center">{quantity}</span>
              <button 
                onClick={() => setQuantity(quantity + 1)}
                className="w-10 h-10 rounded-lg border-2 border-gray-200 flex items-center justify-center text-xl font-bold hover:bg-gray-50 text-gray-700 cursor-pointer"
              >
                +
              </button>
            </div>
          </div>

          {/* Product Intro Box */}
          {/* <div className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-3">
              {t('text_product_detail')}
            </h2>
            <div className="bg-gray-50 rounded-xl p-5 sm:p-6 border border-gray-100">
              <p className="text-[14px] sm:text-[15px] text-gray-600 leading-relaxed m-0 whitespace-pre-wrap">
                {product.description || ""}
              </p>
            </div>
          </div> */}

          {/* Desktop Add to Cart */}
          <div className="hidden md:flex items-center gap-4 mt-auto">
            <button 
              onClick={handleAddToCart}
              className="w-14 h-14 rounded-xl border-2 border-gray-200 flex items-center justify-center text-gray-500 hover:text-black hover:border-black transition-all cursor-pointer"
            >
              <ShoppingCartOutlined className="text-2xl" />
            </button>
            <button 
              onClick={handleBuyNow}
              className="flex-1 bg-[#ed0a3f] hover:bg-[#d40938] text-white text-lg font-bold py-4 rounded-xl transition-colors active:scale-[0.98] shadow-md shadow-pink-500/20"
            >
              {t('text_buy_now_1')}
            </button>
          </div>
        </div>
      </div>

      {/* Product Detailed Introduction Section */}
      <div className="w-full max-w-[1263px] mx-auto px-4 sm:px-6 lg:px-8 mt-16 md:mt-24 mb-10">
        <h2 className="text-2xl font-bold text-gray-900 mb-8 border-b border-gray-100 pb-4">
          {t('text_product_detail')}
        </h2>
        <div className="bg-gray-50 rounded-xl p-5 sm:p-6 border border-gray-100">
        <div className="w-full flex flex-col">
          {product.description && (
            <div 
              className="rich-text-content w-full mb-10"
              dangerouslySetInnerHTML={{ __html: product.description }}
            />
          )}
        </div>
      </div>
      </div>

      {/* Mobile Fixed Bottom Action Bar (Hidden on desktop) */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 px-4 py-3 pb-6 flex items-center gap-4 z-50">
        <div className="flex flex-col items-center justify-center text-gray-600 px-2 cursor-pointer hover:text-black transition-colors" onClick={handleAddToCart}>
          <ShoppingCartOutlined className="text-[26px]" />
        </div>
        <button 
          onClick={handleBuyNow}
          className="flex-1 bg-[#ed0a3f] hover:bg-[#d40938] text-white text-[16px] font-bold py-3 rounded-lg transition-colors active:scale-95"
        >
          {t('text_buy_now_1')}
        </button>
      </div>
    </div>
  );
}
